import { TuiPreview, TuiPreviewDialogService } from '@taiga-ui/kit';
import {
  ChangeDetectionStrategy,
  computed,
  Component,
  DestroyRef,
  inject,
  Input,
  Renderer2,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { AsyncPipe, DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, finalize } from 'rxjs/operators';
import { EMPTY, interval, Subscriber, Subscription } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import {
  RestrictedRequestFileDto,
  RestrictedRequestViewDto,
} from '@outkeep/shared';
import {
  RequestDeviceModelId,
  RequestDeviceModelName,
} from '@core/types/RequestDevice';
import { ThemeSelectorComponent } from '@core/components/theme-selector/theme-selector.component';
import { AccountService } from '@core/services/account';
import { WorkflowLogoutGuardService } from '@core/services/workflow/workflow-logout-guard.service';
import {
  DeviceDetectorService,
  FileDownloadService,
  IsLoadingService,
} from '@core/services';
import { RequestsApiService } from '@core/services/request/requests-api.service';
import { RequestCommentsService } from '@core/services/request';
import { FileExtensionPipe, FilePreviewUrlPipe } from '@core/pipes';
import { FilePreviewComponent } from '@core/components';
import { isImageByFilename } from '@core/helpers';
import { environment } from '@environment/environment';
import { TuiDialogContext, TuiButton } from '@taiga-ui/core';
import {
  RestrictedRequestViewAccessMode,
  RestrictedRequestViewState,
} from './restricted-request-view.types';

@Component({
  selector: 'x-restricted-request-view',
  imports: [
    DatePipe,
    ReactiveFormsModule,
    ThemeSelectorComponent,
    TranslatePipe,
    FilePreviewComponent,
    FilePreviewUrlPipe,
    FileExtensionPipe,
    FormsModule,
    TuiPreview,
    TuiButton,
    AsyncPipe,
  ],
  templateUrl: './restricted-request-view.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RestrictedRequestViewComponent {
  private readonly requestNumberSignal = signal('');
  private readonly guestTokenSignal = signal('');
  readonly $requestNumber = this.requestNumberSignal.asReadonly();
  readonly $guestToken = this.guestTokenSignal.asReadonly();
  readonly $request = signal<RestrictedRequestViewDto | null>(null);
  readonly $commentLoading = signal(false);
  readonly $viewState = signal<RestrictedRequestViewState>('idle');
  readonly $accessMode = computed<RestrictedRequestViewAccessMode>(() =>
    this.$guestToken() ? 'guest' : 'internal',
  );
  readonly $showLogout = computed(() => this.$accessMode() === 'internal');
  readonly newCommentText = new FormControl('', {
    nonNullable: true,
    validators: [Validators.required],
  });

  private readonly requestsApiService = inject(RequestsApiService);
  private readonly accountService = inject(AccountService);
  private readonly workflowLogoutGuard = inject(WorkflowLogoutGuardService);
  private readonly requestCommentsService = inject(RequestCommentsService);
  private readonly isLoadingService = inject(IsLoadingService);
  private readonly fileDownloadService = inject(FileDownloadService);
  private readonly renderer = inject(Renderer2);
  private readonly previewDialogService = inject(TuiPreviewDialogService);
  private readonly destroyRef = inject(DestroyRef);
  private autoRefreshSubscription: Subscription | null = null;
  readonly deviceService = inject(DeviceDetectorService);

  readonly currentPreviewFile = signal<RestrictedRequestFileDto | null>(null);

  @ViewChild('imagePreviewTemplate')
  imagePreviewTemplate!: TemplateRef<TuiDialogContext>;

  @Input({ required: true })
  set requestNumber(value: string) {
    this.requestNumberSignal.set(value);

    if (value) {
      this.loadRestrictedRequest();
      this.startAutoRefresh();
    }
  }

  @Input()
  set token(value: string) {
    this.guestTokenSignal.set(value);

    if (value) {
      this.loadRestrictedRequest();
      this.startAutoRefresh();
    }
  }

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.autoRefreshSubscription?.unsubscribe();
    });
  }

  loadRestrictedRequest(showLoader = true): void {
    const requestNumber = this.$requestNumber();
    const token = this.$guestToken();

    if (!requestNumber && !token) {
      return;
    }

    if (showLoader) {
      this.isLoadingService.start();
    }

    this.$viewState.set(showLoader ? 'loading' : 'idle');

    const request$ = token
      ? this.requestsApiService.getRestrictedRequestByGuestToken$(token)
      : this.requestsApiService.getRestrictedRequest$(requestNumber);

    request$
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (token && error.status === 404) {
            this.$request.set(null);
            this.$viewState.set('not-found');
            this.autoRefreshSubscription?.unsubscribe();

            return EMPTY;
          }

          if (token) {
            this.$request.set(null);
            this.$viewState.set('error');
            this.autoRefreshSubscription?.unsubscribe();

            return EMPTY;
          }

          return EMPTY;
        }),
        finalize(() => {
          if (showLoader) {
            this.isLoadingService.stop();
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(request => {
        this.$request.set(request);
        this.$viewState.set('success');
      });
  }

  startAutoRefresh(): void {
    this.autoRefreshSubscription?.unsubscribe();

    if (this.$viewState() === 'not-found' || this.$viewState() === 'error') {
      return;
    }

    this.autoRefreshSubscription = interval(60_000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadRestrictedRequest(false));
  }

  addComment(): void {
    const text = this.newCommentText.value.trim();
    const token = this.$guestToken();

    if (!text) {
      return;
    }

    this.$commentLoading.set(true);
    const createComment$ = token
      ? this.requestCommentsService.createRestrictedRequestGuestComment$(
          token,
          {
            text,
          },
        )
      : this.requestCommentsService.createRestrictedRequestComment$(
          this.$requestNumber(),
          { text },
        );

    createComment$
      .pipe(
        catchError(() => EMPTY),
        finalize(() => this.$commentLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.newCommentText.reset();
        this.loadRestrictedRequest(false);
      });
  }

  logout(): void {
    void this.workflowLogoutGuard.logout();
  }

  fileIsImage(filename: string | null): boolean {
    if (!filename) return false;

    try {
      return isImageByFilename(filename);
    } catch {
      return false;
    }
  }

  getFileStaticUrl(path: string | null): string {
    return path ? environment.staticUrl + path : '';
  }

  downloadFile(file: RestrictedRequestFileDto): void {
    if (!file.path) return;

    const filename = this.getFileName(file);

    if (filename.toLowerCase().endsWith('.pdf')) {
      this.fileDownloadService.openPdfByPath(file.path, filename);

      return;
    }

    this.fileDownloadService.downloadFile(
      environment.downloadFilesUrl + file.path,
      filename,
      false,
      this.renderer,
    );
  }

  getFilesByCategory(files: RestrictedRequestViewDto['files']): {
    categoryId: number;
    categoryName: string | null;
    files: RestrictedRequestViewDto['files'];
  }[] {
    const map = new Map<
      number,
      {
        categoryId: number;
        categoryName: string | null;
        files: RestrictedRequestViewDto['files'];
      }
    >();

    for (const file of files) {
      if (!map.has(file.category.id)) {
        map.set(file.category.id, {
          categoryId: file.category.id,
          categoryName: file.category.name,
          files: [],
        });
      }

      map.get(file.category.id)!.files.push(file);
    }

    return Array.from(map.values());
  }

  getFileName(file: RestrictedRequestViewDto['files'][number]): string {
    return file.originFilename || file.filename || file.path || '';
  }

  getProductName(
    product: RestrictedRequestViewDto['products'][number],
  ): string | null {
    const amount = product.amount ? ` x${product.amount}` : '';

    return product.product.name ? `${product.product.name}${amount}` : null;
  }

  getDeviceModelName(modelId: number | null): string {
    switch (modelId) {
      case RequestDeviceModelId.INVIS_DUOS:
        return RequestDeviceModelName.INVIS_DUOS;
      case RequestDeviceModelId.INVIS_DUOS_S:
        return RequestDeviceModelName.INVIS_DUOS_S;
      case RequestDeviceModelId.INVIS_DUOS_3D_L:
        return RequestDeviceModelName.INVIS_DUOS_3D_L;
      case RequestDeviceModelId.INVIS_DUOS_ONLINE_LITE:
        return RequestDeviceModelName.INVIS_DUOS_ONLINE_LITE;
      case RequestDeviceModelId.INVIS_DUOS_CONTAINER:
        return RequestDeviceModelName.INVIS_DUOS_CONTAINER;
      default:
        return modelId ? String(modelId) : '-';
    }
  }

  openImagePreview(file: RestrictedRequestFileDto): void {
    this.currentPreviewFile.set(file);
    this.previewDialogService
      .open(this.imagePreviewTemplate)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  downloadPreviewFile(): void {
    const file = this.currentPreviewFile();

    if (file) this.downloadFile(file);
  }
  public backdropPreviewClickClose(
    event: Event,
    imagePreviewObserver: Subscriber<any>,
  ) {
    if ((event.target as HTMLElement).tagName === 'TUI-PREVIEW') {
      imagePreviewObserver.complete();
    }
  }
}
